import { useState } from "react";
import { useParams, Link } from "react-router";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { IconCamera, IconCheck, IconCircleCheck, IconDownload, IconSettings } from "@tabler/icons-react";
import { useBuildsByEvent, useNvpStatus, useEvent } from "@/api/hooks";
import { NvpStatusDot, type NvpStatus } from "@/components/nvp-status-dot";
import { TeamFlag } from "@/components/team-flag";
import { GateBadge } from "@/components/gate-badge";
import { asEventId } from "@/types/identifiers";

const STATUS_BORDER_COLORS: Record<NvpStatus, string> = {
  online: "border-l-green-500",
  offline: "border-l-red-500",
  unknown: "border-l-border",
};

export function FleetOverviewPage() {
  const { eventId: rawEventId } = useParams();
  const eventId = rawEventId ? asEventId(rawEventId) : undefined;
  const { data: builds, isLoading: buildsLoading } = useBuildsByEvent(eventId!);
  const { data: event } = useEvent(eventId!);
  const { getNvpStatus } = useNvpStatus();
  const [downloading, setDownloading] = useState(false);

  const hasGolden = builds?.some((b) => b.has_golden);

  async function downloadGoldens() {
    if (!eventId) return;
    setDownloading(true);
    try {
      const baseUrl = import.meta.env.VITE_API_URL ?? "";
      const resp = await fetch(
        `${baseUrl}/api/builds/event/${eventId}/export-goldens`,
        { credentials: "include" },
      );
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "golden-calibrations.zip";
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  if (!eventId) {
    return <EmptyState message="No event selected." />;
  }

  return (
    <div>
      <PageHeader
        title="Fleet Overview"
        subtitle={event?.name}
        count={builds?.length}
        countLabel="builds"
        action={
          hasGolden ? (
            <Button
              variant="outline"
              size="sm"
              disabled={downloading}
              onClick={downloadGoldens}
              className="gap-1.5 text-xs"
            >
              <IconDownload className="h-3.5 w-3.5" />
              {downloading ? "Exporting…" : "Export Goldens"}
            </Button>
          ) : undefined
        }
      />

      {buildsLoading ? (
        <div className="mt-6 space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : !builds || builds.length === 0 ? (
        <EmptyState message="No builds for this event." className="mt-6" />
      ) : (
        <div className="mt-6 flex flex-col gap-2">
          {[...builds].sort((a, b) => (a.boat_code ?? "").localeCompare(b.boat_code ?? "")).map((build) => {
            const status = getNvpStatus(build.boat_code);
            return (
              <Link
                key={build.id}
                to={`/events/${eventId}/builds/${build.id}`}
                className={`flex flex-col gap-1.5 sm:flex-row sm:items-center bg-card border-l-3 rounded-r-lg pl-4 pr-5 py-2.5 sm:py-0 sm:h-14 transition-shadow hover:shadow-[0_1px_4px_rgba(0,0,0,0.04)] ${STATUS_BORDER_COLORS[status]}`}
              >
                {/* Status dot + Team info */}
                <div className="flex items-center flex-1 min-w-0">
                  <div className="mr-3.5">
                    <NvpStatusDot status={status} />
                  </div>
                  <TeamFlag
                    iso_alpha2={build.team_iso_alpha2 ?? ""}
                    name={build.team_name ?? undefined}
                    size="xs"
                  />
                  <span className="font-medium text-[13px] ml-2 truncate">
                    {build.team_name ?? "-"}
                  </span>
                  <span className="text-xs text-muted-foreground/50 ml-2 shrink-0 whitespace-nowrap">
                    {build.boat_code ?? build.boat_number ?? ""}
                  </span>
                </div>

                {/* Status icons + Counts */}
                <div className="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 text-xs text-muted-foreground shrink-0">
                  {build.active_weighing_gate_ok === false && <GateBadge label="Weighing gate" />}
                  {build.signed_off_at && (
                    <IconCircleCheck className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                  )}
                  {build.has_golden && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400">
                      Golden
                    </span>
                  )}
                  {build.has_signed_off_toe_check && (
                    <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                      <IconCheck className="h-3.5 w-3.5" />
                      Toe
                    </span>
                  )}
                  {build.has_signed_off_yaw_check && (
                    <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                      <IconCheck className="h-3.5 w-3.5" />
                      Yaw
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1">
                    <IconCamera className="h-3.5 w-3.5" />
                    {build.photo_count}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <IconSettings className="h-3.5 w-3.5" />
                    {build.calibration_count}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
